import { defineMessages, useIntl } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import * as tracking from 'dibs-tracking';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { getNumberOfTiles } from '../../utils/adHelpers.ts';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions.js';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import {
    isLatestPage,
    getSelectedValue,
    setSelectedValue,
    getPageSizeSelectOptions,
} from './sbRespPageSizeHelpers.js';

// components
import { BasicSelect } from 'dibs-elements/exports/BasicSelect.tsx';

// styles
import styles from './SbRespPageSize.scss';

const messages = defineMessages({
    label: { id: 'sb.SbRespPageSizeComponent.label', defaultMessage: 'Items per page' },
});

function trackPageSizeEvent(pageSizeValue) {
    tracking.trackEvent({
        category: 'results page refinement',
        action: 'results displayed',
        label: `${pageSizeValue} results clicked`,
        value: 0,
    });
}

function PageSizeLinks({ options, onChange, pageSize, areDisplayAdsEnabled }) {
    return options.map(({ value }) => {
        const isSelected = getSelectedValue(pageSize) === value;
        return (
            <span
                key={value}
                className={classNames(styles.link, { [styles.selected]: isSelected })}
                onClick={() => {
                    if (!isSelected) {
                        onChange({ target: { value } }, areDisplayAdsEnabled);
                    }
                }}
            >
                {value}
            </span>
        );
    });
}

function SbRespPageSizeComponent({ onChange = () => {}, pageSize, itemSearch, usePageSizeLinks }) {
    const intl = useIntl();
    const { appliedFilters } = itemSearch;
    const options = getPageSizeSelectOptions(intl);

    if (isLatestPage(appliedFilters)) {
        return null;
    }

    return (
        <ServerVarsContextConsumer>
            {({ areDisplayAdsEnabled }) => {
                return usePageSizeLinks ? (
                    <PageSizeLinks
                        pageSize={pageSize}
                        onChange={onChange}
                        options={options}
                        areDisplayAdsEnabled={areDisplayAdsEnabled}
                    />
                ) : (
                    <div className={styles.container}>
                        <BasicSelect
                            ariaLabel={intl.formatMessage(messages.label)}
                            size="medium"
                            options={options}
                            onChange={e => onChange(e, areDisplayAdsEnabled)}
                            dataTn="sb-resp-page-size"
                            value={getSelectedValue(pageSize)}
                            isArrowFocusable={false}
                        />
                    </div>
                );
            }}
        </ServerVarsContextConsumer>
    );
}

SbRespPageSizeComponent.propTypes = {
    onChange: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    itemSearch: PropTypes.object.isRequired,
    usePageSizeLinks: PropTypes.bool,
};

const mapStateToProps = state => {
    const { relayVariables } = state;
    const {
        variables: { first },
    } = relayVariables;
    return {
        pageSize: first,
    };
};

function mapDispatchToProps(dispatch, props) {
    return {
        onChange(e, areDisplayAdsEnabled) {
            const { itemSearch } = props;
            const { displayUriRef } = itemSearch;

            // set page size in cookies
            setSelectedValue(e.target.value);

            // track event in GA
            trackPageSizeEvent(e.target.value);

            // update relay variables
            dispatch(
                updateRelayVariables({
                    first: getNumberOfTiles({
                        pageSize: parseInt(e.target.value, 10),
                        path: displayUriRef,
                        areDisplayAdsEnabled,
                    }),
                })
            );
        },
    };
}

export const SbRespPageSize = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(SbRespPageSizeComponent),
    {
        itemSearch: graphql`
            fragment SbRespPageSize_itemSearch on ItemSearchQueryConnection {
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
                displayUriRef
            }
        `,
    }
);
